import type { StreamHealth } from './conn-state';
import type { ModelView } from '../../core/domain/model';
import type { ModelSource, ModelUpsert, HistorySample } from '../../core/bridge/connect-client';
import type { StreamFrame } from '../../core/bridge/types';
import type { BackendClient } from '../bridge/backend-client';

/*
 * ModelManager — main-thread PROXY of the canonical model manager in the Backend
 * Worker. Mirrors ControllerManager: opens ONE streaming subscription (a
 * MessagePort), maintains a local cache from snapshot frames, re-emits `change`.
 * The subscription is registry-ref-counted in the worker, so the upstream
 * Models.Subscribe is held only while a component is watching (start) and torn
 * down on stop — fetch/release only while watching.
 *
 * Unlike the read-only ControllerManager, models are interactive, so this also
 * exposes the lifecycle/source ACTIONS (forwarded to the worker): start/stop,
 * the full command surface, on-demand + hover-prefetch SCXML source, and the
 * one write verb. Components call these on the manager; the worker owns the IO.
 */
export class ModelManager extends EventTarget {
  private state: StreamHealth = 'live';
  private readonly cache = new Map<string, ModelView>();
  private port?: MessagePort;

  constructor(
    private readonly backend: BackendClient,
    private readonly gatewayId: string,
  ) {
    super();
  }

  get all(): ModelView[] {
    return [...this.cache.values()];
  }

  get(id: string): ModelView | undefined {
    return this.cache.get(id);
  }

  async start(): Promise<void> {
    this.state = 'live';
    this.port = await this.backend.api.subscribeModels(this.gatewayId);
    this.port.onmessage = (e: MessageEvent<StreamFrame<ModelView>>) => this.onFrame(e.data);
    this.port.start();
  }

  stop(): void {
    this.port?.postMessage({ kind: 'close' });
    this.port = undefined;
  }

  // ── actions (forwarded to the worker; the worker owns the network IO) ──────

  /** Start a model — reserved lifecycle command; persists desired_state. */
  startModel(id: string): Promise<void> {
    return this.backend.api.executeModelCommand(this.gatewayId, id, 'start');
  }

  /** Stop a model — reserved lifecycle command; persists desired_state. */
  stopModel(id: string): Promise<void> {
    return this.backend.api.executeModelCommand(this.gatewayId, id, 'stop');
  }

  /** Any command — reserved lifecycle (pause/resume/reset) or a declared one. */
  execute(id: string, command: string, eventData?: Record<string, string>): Promise<void> {
    return this.backend.api.executeModelCommand(this.gatewayId, id, command, eventData);
  }

  /** Bring a model into being. Refuses an id that already exists — that is `update`'s. */
  create(id: string, m: ModelUpsert): Promise<void> {
    return this.backend.api.createModel(this.gatewayId, id, m);
  }

  /** Edit a model that exists — deltas by mask, or ["*"] for an authoring rewrite. */
  update(id: string, m: ModelUpsert): Promise<void> {
    return this.backend.api.updateModel(this.gatewayId, id, m);
  }

  /** Remove a model. The list updates from the subscription, like every other change — this does
   *  not touch the local cache, so the screen reflects what the fabric actually did. */
  delete(id: string): Promise<void> {
    return this.backend.api.deleteModel(this.gatewayId, id);
  }

  /** Fetch the full SCXML body (served from the prefetch cache when warm, unless `fresh`). */
  getSource(id: string, fresh = false): Promise<ModelSource> {
    return this.backend.api.getModelSource(this.gatewayId, id, fresh);
  }

  /** Warm the source cache on hover so the detail editor opens instantly. */
  prefetchSource(id: string): void {
    void this.backend.api.prefetchModelSource(this.gatewayId, id);
  }

  // ── live variable values + logs (per-model streams; ref-counted in the
  //    worker registry — open while a section watches, torn down on leave) ───

  /** Live published-variable values: delta frames of TypedValue (symbolId =
   *  variable id). The consumer seeds its chart buffers via queryHistory. */
  subscribeValues(id: string): Promise<MessagePort> {
    return this.backend.api.subscribeModelValues(this.gatewayId, id);
  }

  /** Live IP-safe log tail (no replay): delta frames of ModelLog. */
  subscribeLogs(id: string): Promise<MessagePort> {
    return this.backend.api.subscribeModelLogs(this.gatewayId, id);
  }

  /** Chart-buffer seed for the model's variables — the historian blanket-
   *  captures model variable values keyed `model:<mid>`, so this is the SAME
   *  queryRange the symbol grid seeds from (default window 10s). */
  queryHistory(id: string, variableIds: string[], windowMs = 10_000): Promise<HistorySample[]> {
    const now = Date.now();
    return this.backend.api.queryRange(this.gatewayId, `model:${id}`, variableIds, now - windowMs, now);
  }

  // ── bound-INPUT side (the model's arguments are controller symbols; their
  //    charts ride the symbol grid's exact windowed stream + seed path) ──────

  /** Windowed live values for a model input's bound controller symbols. */
  subscribeBoundValues(controllerId: string, symbolPaths: string[]): Promise<MessagePort> {
    return this.backend.api.subscribeGridValues(this.gatewayId, controllerId, symbolPaths);
  }

  /** Chart-buffer seed for bound controller symbols (default window 10s). */
  queryBoundHistory(controllerId: string, symbolPaths: string[], windowMs = 10_000): Promise<HistorySample[]> {
    const now = Date.now();
    return this.backend.api.queryRange(this.gatewayId, controllerId, symbolPaths, now - windowMs, now);
  }

  /** Whether this roster is flowing — part of what a gateway's badge claims. Starts
   *  `live`: no completion marker, so only a break is observable (see conn-state). */
  get health(): StreamHealth {
    return this.state;
  }

  private onFrame(frame: StreamFrame<ModelView>): void {
    switch (frame.kind) {
      case 'snapshot':
        // Revision 0 is the worker's boot-from-cache frame: last session's roster,
        // painted before the network has said anything. Worth showing, not worth
        // vouching for — it must not clear a break.
        if (frame.revision > 0) this.state = 'live';
        this.cache.clear();
        for (const m of frame.items) this.cache.set(m.id, m);
        break;
      case 'delta':
        this.state = 'live';
        if (frame.op === 'remove') this.cache.delete(frame.item.id);
        else this.cache.set(frame.item.id, frame.item);
        break;
      case 'error':
        // Keep the cache (see ControllerManager.onFrame) but stop vouching for it.
        // This used to return silently, so nothing on screen could ever learn of it.
        this.state = 'down';
        break;
    }
    this.dispatchEvent(new CustomEvent('change'));
  }
}
